import { useState, useEffect, useCallback, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import SubLayout from '@/components/feature/SubLayout';
import { supabase } from '@/lib/supabase';
import { useSubcontractorAssignments } from '@/hooks/useSubcontractorAssignments';
import { useStageScheduleChecklist } from '@/hooks/useStageScheduleChecklist';
import { useProjectSummary } from '@/hooks/useProjectSummary';
import { useTranslation } from 'react-i18next';

// ─── Types ────────────────────────────────────────────────────────────────────

interface InstructionSummary {
  id: string;
  instruction_title: string;
  project_id: string;
  status: string;
  priority: string;
  due_at: string | null;
  created_at: string;
}

interface ProjectInstructionsMap {
  [projectId: string]: {
    total: number;
    urgent: number;
    latest: InstructionSummary | null;
  };
}

interface DashboardProject {
  id: string;
  project_name: string;
  site_address: string | null;
  cover_image: string | null;
  current_stage: string | null;
  progress: number;
}

const FALLBACK_COVER = 'https://readdy.ai/api/search-image?query=modern%20residential%20construction%20site%20New%20Zealand%20home%20building%20timber%20framing%20progress%20professional%20photography%20warm%20natural%20light%20clean%20background%20minimal&width=800&height=400&seq=subfallback&orientation=landscape';

function isUrgent(due: string | null): boolean {
  if (!due) return false;
  const days = Math.round((new Date(due).getTime() - Date.now()) / 86400000);
  return days <= 1 && days >= 0;
}

// ─── Memoized card image to prevent flicker from re-renders ──────────────────

interface CardImageProps {
  src: string | null | undefined;
  alt: string;
}

const CardImage = memo(function CardImage({ src, alt }: CardImageProps) {
  const [failed, setFailed] = useState(false);
  const imageSrc = failed || !src ? FALLBACK_COVER : src;

  return (
    <img
      src={imageSrc}
      alt={alt}
      className="w-full h-full object-cover object-top"
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
    />
  );
});

// ─── Project Card with LIVE summary ──────────────────────────────────────

interface ProjectCardProps {
  project: DashboardProject;
  ins: ProjectInstructionsMap[string];
  subInfo: { pending: number; total: number; checked: number } | undefined;
  onClick: () => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}

function DashboardProjectCard({ project, ins, subInfo, onClick, t }: ProjectCardProps) {
  const summary = useProjectSummary(project.id);
  const latest = ins?.latest;
  const hasUrgent = (ins?.urgent ?? 0) > 0;

  // Live values override stale assignment data
  const liveStage = summary.loaded ? summary.activeStage : (project.current_stage || t('status.planning'));
  const liveProgress = summary.loaded ? summary.projectProgress : (project.progress || 0);
  const isCompleted = summary.loaded && summary.projectProgress >= 100;

  function fmtDueDate(d: string | null): string {
    if (!d) return '—';
    const days = Math.round((new Date(d).getTime() - Date.now()) / 86400000);
    if (days < 0) return t('sub.dashboard.overdue');
    if (days === 0) return t('sub.dashboard.dueToday');
    if (days === 1) return t('sub.dashboard.dueTomorrow');
    return t('sub.dashboard.dueSoon', { date: new Date(d).toLocaleDateString(t('date.locale') || 'en-NZ', { day: 'numeric', month: 'short' }) });
  }

  function getStatusLabel(status: string): string {
    switch (status) {
      case 'open': return t('sub.dashboard.statusOpen');
      case 'in_review': return t('sub.dashboard.statusInReview');
      case 'resolved': return t('sub.dashboard.statusResolved');
      default: return status;
    }
  }

  const STATUS_BG: Record<string, string> = {
    open: '#FEF0E7',
    in_review: '#FFFBEB',
    resolved: '#F0FDF4',
  };

  const STATUS_COLOR: Record<string, string> = {
    open: '#E8621A',
    in_review: '#F59E0B',
    resolved: '#059669',
  };

  return (
    <button
      onClick={onClick}
      className="w-full bg-white rounded-2xl border border-gray-100 overflow-hidden text-left cursor-pointer active:scale-[0.99] transition-transform will-change-transform"
    >
      {/* Photo */}
      <div className="h-52 relative overflow-hidden">
        <CardImage src={project.cover_image} alt={project.project_name} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/80 via-[#1A1A1A]/10 to-transparent pointer-events-none"></div>
        {(ins?.total ?? 0) > 0 && (
          <div className="absolute top-3 right-3 bg-[#EF4444] text-white text-[10px] font-bold px-2 py-0.5 rounded-full pointer-events-none">
            {ins.total} {t('sub.dashboard.due')}
          </div>
        )}
        <div className="absolute bottom-3 left-3 right-3 pointer-events-none">
          <div className="text-white font-bold text-[15px] leading-tight">{project.project_name}</div>
          <div className="text-white/60 text-xs mt-0.5 truncate">{project.site_address || '—'}</div>
        </div>
      </div>

      {/* Stage + progress */}
      <div className="px-4 py-3 space-y-2.5">
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-1.5">
              <i className="ri-tools-line text-[10px] text-[#9CA3AF]"></i>
              <span className="text-[#6B7280] text-xs font-medium">
                {isCompleted ? t('sub.dashboard.statusCompleted') : liveStage}
              </span>
            </div>
            <span className="text-[#059669] text-xs font-bold">{liveProgress}%</span>
          </div>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full bg-[#059669]"
              style={{ width: `${liveProgress}%` }}
            ></div>
          </div>
        </div>

        {/* Latest instruction */}
        {latest && (
          <div className={`flex items-start gap-2 px-3 py-2 rounded-xl ${hasUrgent ? 'bg-[#FEF2F2]' : 'bg-[#F9FAFB]'}`}>
            <i className={`ri-file-list-3-line text-xs mt-0.5 flex-shrink-0 ${hasUrgent ? 'text-[#EF4444]' : 'text-[#9CA3AF]'}`}></i>
            <div className="flex-1 min-w-0">
              <div className={`text-xs font-semibold leading-snug truncate ${hasUrgent ? 'text-[#EF4444]' : 'text-[#374151]'}`}>
                {latest.instruction_title}
              </div>
              <div className={`text-[10px] mt-0.5 font-semibold ${hasUrgent ? 'text-[#EF4444]' : 'text-[#9CA3AF]'}`}>
                {fmtDueDate(latest.due_at)}
              </div>
            </div>
          </div>
        )}
        {/* Subcontractor checklist pending */}
        {subInfo && subInfo.pending > 0 && (
          <div className="flex items-start gap-2 px-3 py-2 rounded-xl bg-[#FFFBEB]">
            <i className="ri-checkbox-multiple-line text-xs mt-0.5 flex-shrink-0 text-[#F59E0B]"></i>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold leading-snug text-[#92400E] truncate">
                {subInfo.pending} {t('sub.stage.subcontractorCheckProgress')}
              </div>
              <div className="text-[10px] mt-0.5 text-[#F59E0B]">
                {subInfo.checked}/{subInfo.total} {t('sub.stage.completed')}
              </div>
            </div>
          </div>
        )}

        {/* Latest instruction status badge (in the list below) */}
      </div>
    </button>
  );

  // This is unreachable but satisfies TS — getStatusLabel and STATUS_BG/COLOR are scoped to inner fn above
  void getStatusLabel; void STATUS_BG; void STATUS_COLOR;
}

// ─── Dashboard ─────────────────────────────────────────────────────────────

export default function SubcontractorDashboard() {
  const navigate = useNavigate();
  const { projects, projectIds, loading: projectsLoading, error, refetch } = useSubcontractorAssignments();

  // Re-fetch when any project's schedule/checklist/stage changes
  useEffect(() => {
    const handler = () => {
      if (import.meta.env.DEV) console.log('[SubDashboard] schedule sync event — refetching assignments');
      refetch();
    };
    window.addEventListener('__jphph_schedule_sync__', handler);
    return () => window.removeEventListener('__jphph_schedule_sync__', handler);
  }, [refetch]);

  const [instructions, setInstructions] = useState<InstructionSummary[]>([]);
  const [instructionsLoading, setInstructionsLoading] = useState(true);
  const [insError, setInsError] = useState('');

  // Load instructions for assigned projects
  const loadInstructions = useCallback(async () => {
    setInstructionsLoading(true);
    setInsError('');

    try {
      if (projectIds.length === 0) {
        setInstructions([]);
        setInstructionsLoading(false);
        return;
      }

      const { data, error: insErr } = await supabase
        .from('jphph_instructions')
        .select('id, instruction_title, project_id, status, priority, due_at, created_at')
        .in('project_id', projectIds)
        .order('created_at', { ascending: false });

      if (import.meta.env.DEV) {
        console.log('[SubDashboard] instructions query:', { projectIds, count: data?.length ?? 0, error: insErr });
      }

      if (insErr) {
        console.error('[SubDashboard] instructions error:', insErr);
        setInsError(insErr.message);
      }

      setInstructions((data as InstructionSummary[]) ?? []);
    } catch (e) {
      setInsError(String(e));
    } finally {
      setInstructionsLoading(false);
    }
  }, [projectIds]);

  // Load sub-pending checklist counts for all assigned projects
  const [subPendingMap, setSubPendingMap] = useState<Record<string, { pending: number; total: number; checked: number }>>();
  const loadSubPending = useCallback(async () => {
    if (projectIds.length === 0) {
      setSubPendingMap({});
      return;
    }
    try {
      const { data } = await supabase
        .from('jphph_checklist_items')
        .select('id, project_id, requires_subcontractor_check, subcontractor_checked')
        .in('project_id', projectIds);
      const rows = (data ?? []) as Array<{ project_id: string; requires_subcontractor_check: boolean; subcontractor_checked: boolean }>;
      const map: Record<string, { pending: number; total: number; checked: number }> = {};
      for (const r of rows) {
        if (r.requires_subcontractor_check) {
          if (!map[r.project_id]) map[r.project_id] = { pending: 0, total: 0, checked: 0 };
          map[r.project_id].total += 1;
          if (r.subcontractor_checked) {
            map[r.project_id].checked += 1;
          } else {
            map[r.project_id].pending += 1;
          }
        }
      }
      setSubPendingMap(map);
    } catch {
      // ignore
    }
  }, [projectIds]);

  useEffect(() => {
    if (projectIds.length > 0) {
      loadInstructions();
      loadSubPending();
    } else {
      setInstructions([]);
      setSubPendingMap({});
      setInstructionsLoading(false);
    }
  }, [projectIds, loadInstructions, loadSubPending]);

  const totalSubPending = Object.values(subPendingMap ?? {}).reduce((sum, p) => sum + p.pending, 0);

  // Derive per-project instruction stats
  const projectInsMap: ProjectInstructionsMap = {};
  for (const p of projects) {
    const projIns = instructions.filter((i) => i.project_id === p.id && i.status !== 'resolved');
    projectInsMap[p.id] = {
      total: projIns.length,
      urgent: projIns.filter((i) => isUrgent(i.due_at)).length,
      latest: projIns[0] || null,
    };
  }

  const totalInstructions = instructions.filter((i) => i.status !== 'resolved').length;
  const urgentCount = instructions.filter((i) => i.status !== 'resolved' && isUrgent(i.due_at)).length;

  const { t } = useTranslation();
  const loading = projectsLoading || instructionsLoading;

  function getStatusLabel(status: string): string {
    switch (status) {
      case 'open': return t('sub.dashboard.statusOpen');
      case 'in_review': return t('sub.dashboard.statusInReview');
      case 'resolved': return t('sub.dashboard.statusResolved');
      default: return status;
    }
  }

  const STATUS_BG: Record<string, string> = {
    open: '#FEF0E7',
    in_review: '#FFFBEB',
    resolved: '#F0FDF4',
  };

  const STATUS_COLOR: Record<string, string> = {
    open: '#E8621A',
    in_review: '#F59E0B',
    resolved: '#059669',
  };

  return (
    <SubLayout>
      <div className="w-full overflow-x-hidden px-4 lg:px-8 pt-5 pb-28 lg:pb-10">
        {/* Header */}
        <div className="mb-5">
          <div className="inline-flex items-center gap-1.5 bg-[#ECFDF5] px-2.5 py-1 rounded-full mb-2">
            <i className="ri-tools-line text-[10px] text-[#059669]"></i>
            <span className="text-[10px] font-bold text-[#059669] uppercase tracking-wider">{t('sub.dashboard.subcontractor')}</span>
          </div>
          <h1 className="text-[22px] font-bold text-[#1A1A1A] leading-tight">{t('sub.dashboard.title')}</h1>
          <p className="text-[#9CA3AF] text-sm mt-0.5">
            {projects.length} {t('sub.dashboard.projectsCount', { count: projects.length, s: projects.length !== 1 ? 's' : '' })} · {totalInstructions} {t('sub.dashboard.instructionsCount', { count: totalInstructions, s: totalInstructions !== 1 ? 's' : '' })}
          </p>
        </div>

        {/* Loading */}
        {loading && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-2.5 mb-5">
              {[0,1,2,3].map((i) => (
                <div key={i} className="bg-white rounded-2xl p-3.5 flex items-center gap-3 border border-gray-100">
                  <div className="w-9 h-9 rounded-xl bg-[#E5E7EB] animate-pulse flex-shrink-0" />
                  <div className="space-y-1.5 flex-1">
                    <div className="h-5 bg-[#E5E7EB] rounded animate-pulse w-8" />
                    <div className="h-3 bg-[#E5E7EB] rounded animate-pulse w-16" />
                  </div>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {[0,1].map((i) => (
                <div key={i} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                  <div className="h-52 bg-[#E5E7EB] animate-pulse" />
                  <div className="px-4 py-3 space-y-2.5">
                    <div className="flex items-center justify-between">
                      <div className="h-4 bg-[#E5E7EB] rounded animate-pulse w-[45%]" />
                      <div className="h-4 bg-[#E5E7EB] rounded animate-pulse w-[20%]" />
                    </div>
                    <div className="h-1.5 bg-[#E5E7EB] rounded-full animate-pulse" />
                    <div className="h-3 bg-[#E5E7EB] rounded animate-pulse w-[50%]" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="mb-4 flex items-start gap-2 bg-[#FEF2F2] border border-[#FECACA] rounded-2xl p-4">
            <i className="ri-error-warning-line text-[#EF4444] text-base mt-0.5 flex-shrink-0"></i>
            <div>
              <p className="text-[#EF4444] font-semibold text-sm">{t('sub.dashboard.failedToLoad')}</p>
              <p className="text-[#EF4444] text-xs mt-0.5">{error}</p>
            </div>
          </div>
        )}

        {/* Subcontractor checklist pending alert */}
        {!loading && totalSubPending > 0 && (
          <button
            onClick={() => {
              const firstProjectWithPending = projects.find((p) => {
                const subInfo = subPendingMap[p.id];
                return subInfo && subInfo.pending > 0;
              });
              if (firstProjectWithPending) {
                navigate(`/subcontractor/project/${firstProjectWithPending.id}?tab=Checklist`);
              }
            }}
            className="w-full bg-[#FFFBEB] border border-[#FDE68A] rounded-2xl p-4 mb-5 flex items-center gap-3 cursor-pointer text-left"
          >
            <div className="w-9 h-9 rounded-xl bg-[#F59E0B]/10 flex items-center justify-center flex-shrink-0">
              <i className="ri-checkbox-multiple-line text-[#F59E0B] text-lg"></i>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[#92400E] font-bold text-sm">{t('sub.dashboard.subChecksPending', { count: totalSubPending, s: totalSubPending === 1 ? 'check' : 'checks' })}</div>
              <div className="text-[#374151] text-xs mt-0.5 truncate">
                {t('sub.dashboard.tapToOpenChecklist')}
              </div>
            </div>
            <i className="ri-arrow-right-s-line text-[#F59E0B] flex-shrink-0"></i>
          </button>
        )}

        {/* Urgent instructions alert */}
        {!loading && urgentCount > 0 && (
          <button
            onClick={() => navigate('/subcontractor/instructions')}
            className="w-full bg-[#FEF2F2] border border-[#FECACA] rounded-2xl p-4 mb-5 flex items-center gap-3 cursor-pointer text-left"
          >
            <div className="w-9 h-9 rounded-xl bg-[#EF4444]/10 flex items-center justify-center flex-shrink-0">
              <i className="ri-error-warning-line text-[#EF4444] text-lg"></i>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[#EF4444] font-bold text-sm">{t('sub.dashboard.actionRequired')}</div>
              <div className="text-[#374151] text-xs mt-0.5 truncate">
                {t('sub.dashboard.instructionsDueSoon', { count: urgentCount, s: urgentCount > 1 ? 's' : '' })}
              </div>
            </div>
            <i className="ri-arrow-right-s-line text-[#EF4444] flex-shrink-0"></i>
          </button>
        )}

        {/* Assigned Projects */}
        {!loading && projects.length > 0 && (
          <div className="mb-5">
            <h2 className="text-[15px] font-bold text-[#1A1A1A] mb-3">{t('sub.dashboard.assignedProjects')}</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {projects.map((project) => {
              const ins = projectInsMap[project.id];
              const subInfo = subPendingMap?.[project.id];
              return (
                <DashboardProjectCard
                  key={project.id}
                  project={project}
                  ins={ins}
                  subInfo={subInfo}
                  onClick={() => navigate(`/subcontractor/project/${project.id}`)}
                  t={t}
                />
              );
            })}
            </div>
          </div>
        )}

        {/* No projects assigned */}
        {!loading && projects.length === 0 && !error && (
          <div className="text-center py-16">
            <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3 text-[#D1D5DB]">
              <i className="ri-building-2-line text-5xl"></i>
            </div>
            <p className="text-[#374151] font-semibold mb-1">{t('sub.dashboard.noProjectsAssigned')}</p>
            <p className="text-[#9CA3AF] text-sm max-w-xs mx-auto">
              {t('sub.dashboard.noProjectsAssignedDesc')}
            </p>
          </div>
        )}

        {/* All Instructions */}
        {!loading && totalInstructions > 0 && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-[15px] font-bold text-[#1A1A1A]">{t('sub.dashboard.instructionsTitle')}</h2>
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#FEF0E7]">
                <i className="ri-building-2-line text-[10px] text-[#E8621A]"></i>
                <span className="text-[10px] font-bold text-[#E8621A]">{t('sub.dashboard.instructionsFromMgmt')}</span>
              </div>
            </div>
            <div className="space-y-2">
              {instructions
                .filter((i) => i.status !== 'resolved')
                .map((ins) => {
                  const proj = projects.find((p) => p.id === ins.project_id);
                  const urgent = ins.due_at ? Math.round((new Date(ins.due_at).getTime() - Date.now()) / 86400000) <= 1 : false;
                  return (
                    <button
                      key={ins.id}
                      onClick={() => navigate('/subcontractor/instructions')}
                      className={`w-full bg-white rounded-2xl border text-left p-4 cursor-pointer flex items-center gap-3 ${urgent ? 'border-[#EF4444]/30' : 'border-gray-100'}`}
                    >
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${urgent ? 'bg-[#EF4444]' : 'bg-[#D1D5DB]'}`}></div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[#374151] text-sm font-medium leading-snug truncate">{ins.instruction_title}</div>
                        <div className="text-[#9CA3AF] text-xs mt-0.5 truncate">{proj?.project_name || t('sub.dashboard.unknownProject')}</div>
                      </div>
                      <span
                        className="text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 whitespace-nowrap"
                        style={{
                          backgroundColor: STATUS_BG[ins.status] || '#F9FAFB',
                          color: STATUS_COLOR[ins.status] || '#9CA3AF',
                        }}
                      >
                        {getStatusLabel(ins.status)}
                      </span>
                    </button>
                  );
                })}
            </div>
          </div>
        )}

        {/* Empty instructions */}
        {!loading && projects.length > 0 && totalInstructions === 0 && !insError && (
          <div className="text-center py-8">
            <p className="text-[#9CA3AF] text-sm">{t('sub.dashboard.noActiveInstructions')}</p>
          </div>
        )}
      </div>
    </SubLayout>
  );
}